/**
 * Activity Screen - MOBILE ONLY
 * 
 * Global activity feed showing recent events across all portfolios.
 * Deep-links into relevant opportunities.
 * Uses real data from useActivityFeed hook per Rule 6.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, RefreshControl, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, layout, icon, radius } from '../../shared/tokens/mobile';
import {
  ScreenContainer,
  SectionHeader,
  ListCard,
  ListRow,
} from '../../components/v2/mobile';
import { 
  useActivityFeed, 
  getActivityIcon, 
  getActivityColor,
  ActivityItem 
} from '../../hooks/useActivityFeed';
import { formatRelativeTime } from '../../hooks/useHomeData';

// =============================================================================
// SCREEN
// =============================================================================

export default function ActivityScreen() {
  const router = useRouter();
  const { 
    unreadActivities, 
    recentActivities, 
    unreadCount,
    loading, 
    refreshing, 
    refresh, 
    markAllRead,
    markRead,
    error 
  } = useActivityFeed();

  const handleItemPress = useCallback((item: ActivityItem) => {
    markRead(item.id);
    router.push(`/v2/property/${item.opportunityId}?portfolioId=${item.portfolioId}` as any);
  }, [router, markRead]);

  // Loading state
  if (loading) {
    return (
      <ScreenContainer>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={color.primary} />
          <Text style={styles.loadingText}>Loading activity...</Text>
        </View>
      </ScreenContainer>
    );
  }

  const hasUnread = unreadActivities.length > 0;
  const hasRecent = recentActivities.length > 0;
  const isEmpty = !hasUnread && !hasRecent;

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl 
            refreshing={refreshing} 
            onRefresh={refresh}
            tintColor={color.primary}
          />
        }
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.title}>Activity</Text>
          {unreadCount > 0 && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{unreadCount}</Text>
            </View>
          )}
        </View>

        {/* ERROR STATE */}
        {error && (
          <View style={styles.errorCard}>
            <Ionicons name="alert-circle" size={icon.md} color={color.error} />
            <Text style={styles.errorText}>{error}</Text>
            <Pressable onPress={refresh} style={styles.retryButton}>
              <Text style={styles.retryText}>Retry</Text>
            </Pressable>
          </View>
        )}

        {/* EMPTY STATE */}
        {isEmpty && !error && (
          <View style={styles.emptyContainer}>
            <Ionicons name="notifications-off-outline" size={icon.xxl} color={color.textMuted} />
            <Text style={styles.emptyTitle}>No Activity Yet</Text>
            <Text style={styles.emptySubtitle}>
              Activity will appear here as you work with your opportunities
            </Text>
            <Pressable 
              style={styles.emptyButton}
              onPress={() => router.push('/v2/portfolio' as any)}
            >
              <Text style={styles.emptyButtonText}>View Portfolios</Text>
            </Pressable>
          </View>
        )}

        {/* UNREAD */}
        {hasUnread && (
          <View style={styles.section}>
            <SectionHeader
              label={`Unread (${unreadActivities.length})`}
              actionLabel="Mark all read"
              onAction={markAllRead}
            />
            <ListCard>
              {unreadActivities.map((item, index) => (
                <ListRow
                  key={item.id}
                  iconName={getActivityIcon(item.type) as any}
                  iconColor={getActivityColor(item.type, true)}
                  title={item.title}
                  subtitle={`${item.subtitle} • ${formatRelativeTime(item.timestamp)}`}
                  onPress={() => handleItemPress(item)}
                  hideBorder={index === unreadActivities.length - 1}
                />
              ))}
            </ListCard>
          </View>
        )}

        {/* RECENT */}
        {hasRecent && (
          <View style={styles.section}>
            <SectionHeader label="Recent" />
            <ListCard>
              {recentActivities.slice(0, 10).map((item, index) => (
                <ListRow
                  key={item.id}
                  iconName={getActivityIcon(item.type) as any}
                  iconColor={getActivityColor(item.type, false)}
                  title={item.title}
                  subtitle={`${item.subtitle} • ${formatRelativeTime(item.timestamp)}`}
                  onPress={() => handleItemPress(item)}
                  hideBorder={index === Math.min(recentActivities.length - 1, 9)}
                />
              ))}
            </ListCard>
          </View>
        )}
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: space.md,
    paddingTop: space.md,
    paddingBottom: layout.scrollBottomPadding,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.md,
  },
  loadingText: {
    fontSize: text.body.fontSize,
    color: color.textSecondary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: space.lg,
    gap: space.sm,
  },
  title: {
    fontSize: text.pageTitle.fontSize,
    fontWeight: text.pageTitle.fontWeight,
    lineHeight: text.pageTitle.lineHeight,
    letterSpacing: text.pageTitle.letterSpacing,
    color: color.textPrimary,
  },
  badge: {
    backgroundColor: color.error,
    borderRadius: radius.full,
    paddingHorizontal: space.sm,
    paddingVertical: 2,
    minWidth: 24,
    alignItems: 'center',
  },
  badgeText: {
    fontSize: text.caption.fontSize,
    fontWeight: '700',
    color: color.surface,
  },
  section: {
    marginBottom: space.lg,
  },
  
  // Error
  errorCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: color.errorLight,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.lg,
    gap: space.sm,
  },
  errorText: {
    flex: 1,
    fontSize: text.body.fontSize,
    color: color.error,
  },
  retryButton: {
    paddingHorizontal: space.sm,
    paddingVertical: space.xs,
    backgroundColor: color.error,
    borderRadius: radius.sm,
  },
  retryText: {
    fontSize: text.caption.fontSize,
    color: color.surface,
    fontWeight: '600',
  },

  // Empty state
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: space.xxl,
    gap: space.sm,
  },
  emptyTitle: {
    fontSize: text.cardTitle.fontSize,
    fontWeight: text.cardTitle.fontWeight,
    color: color.textPrimary,
    marginTop: space.md,
  },
  emptySubtitle: {
    fontSize: text.body.fontSize,
    color: color.textMuted,
    textAlign: 'center',
    maxWidth: 280,
  },
  emptyButton: {
    marginTop: space.lg,
    paddingHorizontal: space.lg,
    paddingVertical: space.sm,
    backgroundColor: color.primary,
    borderRadius: radius.md,
  },
  emptyButtonText: {
    fontSize: text.body.fontSize,
    fontWeight: '600',
    color: color.surface,
  },
});
